import * as excel from '@attain-apps/excel'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'

import { Workbook } from '../models'

import { getActionsConfig } from './actions'
import * as effects from './effects'
import * as models from './models'
import { Selectors } from './selectors'
import { State } from './state'

const DEFAULT_DOWNLOAD_FILENAME = 'Attain Apps Download'

/**
 * Executes queries to get entities for the entity collection
 */
export class Plugin<
  TTypeParams extends ec.models.TypeParams.Constraint,
> extends ec.plugins.Plugin<
  TTypeParams,
  typeof Plugin.id,
  State,
  Selectors<TTypeParams>,
  ReturnType<typeof getActionsConfig<TTypeParams>>
> {
  public static readonly id = ec.plugins.Plugin.Id.Custom.create('Excel')

  constructor({
    collection,
    download: downloadPatcher,
  }: Plugin.constructor.Params<TTypeParams>) {
    super({
      actions: getActionsConfig<TTypeParams>(collection),
      collection,
      id: Plugin.id,
      initialState: State.INITIAL,
      selectors: () => new Selectors(this),
    })

    /**
     * Default {@link models.Download.Producer}
     */
    const baseDownloadProducer: models.Download.Producer<TTypeParams> = ({
      attributes,
      entities,
    }) => {
      // Simply creates a workbook with one sheet that has a table containing
      // all of the entities
      const workbook = new Workbook(
        new excel.Sheet(collection.name, new excel.Table(attributes, entities)),
      )
      return {
        filename: DEFAULT_DOWNLOAD_FILENAME,
        workbook,
      }
    }
    /**
     * Applies any patches from the constructor params (i.e.
     * {@link downloadPatcher}) to the default {@link models.Download.Producer}
     * (i.e. {@link baseDownloadProducer})
     */
    const finalDownloadProducer = models.Download.Producer.from(
      baseDownloadProducer,
      downloadPatcher,
    )

    this.feature.registerEffects(effects.download(this, finalDownloadProducer))
  }
}
export namespace Plugin {
  export namespace constructor {
    export interface Params<TTypeParams extends ec.models.TypeParams.Constraint>
      extends Pick<ec.plugins.Plugin.Default<TTypeParams>, 'collection'> {
      download?: models.Download.Producer.Patcher<TTypeParams>
    }
  }
}
